/**
 * Email Send History Panel - Phase 10a.3
 * Updated R6: TO/CC/BCC multi-recipient display with expand/collapse
 *
 * Shows chronological list of all email sends for an entity (invoice/proposal).
 * Per row: timestamp, sender, TO chip (first name + count), CC chip if non-empty,
 * BCC count only, delivery_status badge, custom_note icon.
 * Click "Show all recipients" to expand full TO + CC address lists (BCC stays count-only).
 *
 * Pre-V155 sends (only `recipient` set) render correctly via trigger-synced to_list.
 */
import { useState } from 'react';
import { Loader2, Mail, Users, Clock, User, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { EmailDeliveryStatusBadge } from './EmailDeliveryStatusBadge';
import { useEmailSendHistory } from '@/hooks/useEmailSendHistory';
import type { EmailSend } from '@/hooks/useEmailSendHistory';
import { formatSendDate, isSent, sendEvidencePhrase, type SendEvidence } from '@/lib/sendEvidence';

interface EmailSendHistoryProps {
  entityType: 'invoice' | 'proposal';
  entityId: string;
  /**
   * R3 AC6: the invoice's send as the server's one send rule decided it. A send she recorded herself (Mark as Sent)
   * leaves no Keystone email log row, so it is listed here from this, with the same phrase the invoice page uses.
   */
  sendEvidence?: SendEvidence | null;
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatSentBy(email: string): string {
  const name = email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function emailDisplayName(email: string): string {
  return email.split('@')[0];
}

function SendHistoryItem({ send, labelAsKeystone }: { send: EmailSend; labelAsKeystone?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  // Pre-V155: to_list backfilled via trigger from recipient. Post-V155: full list.
  const toList = send.to_list?.length ? send.to_list : (send.recipient ? [send.recipient] : []);
  const ccList = send.cc_list ?? [];
  const bccCount = send.bcc_list?.length ?? 0;

  const firstTo = toList[0] ?? '';
  const toExtra = toList.length - 1;
  const firstCc = ccList[0] ?? '';
  const ccExtra = ccList.length - 1;

  const canExpand = toList.length > 1 || ccList.length > 0;

  return (
    <div
      className="border rounded-lg p-3 space-y-2"
      data-testid="send-history-item"
    >
      {labelAsKeystone && (
        <p className="text-sm font-medium" data-testid="send-history-keystone-label">
          Emailed from Keystone on {formatSendDate(send.sent_at)}
        </p>
      )}
      {/* Header: date + status badge + optional custom note icon */}
      <div className="flex items-center flex-wrap gap-2">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="w-3.5 h-3.5 shrink-0" />
          {formatDate(send.sent_at)}
        </div>
        <EmailDeliveryStatusBadge status={send.delivery_status} />
        {send.custom_note && (
          <span title="Includes a personal note" className="text-muted-foreground">
            <MessageSquare className="w-3.5 h-3.5" />
          </span>
        )}
      </div>

      {/* TO chip: first address display name + overflow count */}
      <div className="flex items-center gap-1.5 text-sm flex-wrap" data-testid="to-row">
        <span className="text-muted-foreground font-medium text-xs w-8 shrink-0">TO</span>
        <span className="font-medium">{emailDisplayName(firstTo)}</span>
        {toExtra > 0 && (
          <span
            className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full shrink-0"
            data-testid="to-extra-count"
          >
            +{toExtra}
          </span>
        )}
      </div>

      {/* CC chip: only rendered when cc_list non-empty */}
      {ccList.length > 0 && (
        <div className="flex items-center gap-1.5 text-sm flex-wrap" data-testid="cc-row">
          <span className="text-muted-foreground font-medium text-xs w-8 shrink-0">CC</span>
          <span>{emailDisplayName(firstCc)}</span>
          {ccExtra > 0 && (
            <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full shrink-0">
              +{ccExtra}
            </span>
          )}
        </div>
      )}

      {/* BCC: count only — addresses never surfaced for privacy */}
      {bccCount > 0 && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="bcc-count-row">
          <Users className="w-3.5 h-3.5 shrink-0" />
          <span>BCC: {bccCount} {bccCount === 1 ? 'address' : 'addresses'}</span>
        </div>
      )}

      {/* Expand/collapse for full TO + CC address lists */}
      {canExpand && (
        <button
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
          onClick={() => setExpanded((v) => !v)}
          data-testid="expand-recipients"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Show less' : 'Show all recipients'}
        </button>
      )}

      {/* Expanded: full TO + CC lists; BCC stays count-only */}
      {expanded && (
        <div
          className="bg-muted/50 rounded p-2 text-xs space-y-1 break-all"
          data-testid="expanded-recipients"
        >
          <div>
            <span className="text-muted-foreground font-medium">TO: </span>
            {toList.join(', ')}
          </div>
          {ccList.length > 0 && (
            <div>
              <span className="text-muted-foreground font-medium">CC: </span>
              {ccList.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Sent by */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <User className="w-3 h-3 shrink-0" />
        Sent by {formatSentBy(send.sent_by)}
      </div>
    </div>
  );
}

/** A send a person recorded (Mark as Sent): a date and a name, never recipients, attachments or a delivery status. */
function RecordedSendItem({ evidence }: { evidence: SendEvidence }) {
  return (
    <div className="border rounded-lg p-3 space-y-1" data-testid="send-history-recorded">
      <div className="flex items-center gap-1.5 text-sm font-medium">
        <User className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        {sendEvidencePhrase(evidence)}
      </div>
      <p className="text-xs text-muted-foreground">
        Keystone did not email this one. It was recorded as sent.
      </p>
    </div>
  );
}

export function EmailSendHistory({ entityType, entityId, sendEvidence }: EmailSendHistoryProps) {
  const { data, isLoading, error } = useEmailSendHistory(entityType, entityId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="send-history-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground" data-testid="send-history-error">
        Failed to load send history.
      </div>
    );
  }

  const sends = data?.items ?? [];
  // A recorded send is listed unless it is the Keystone email already in the log above it.
  const recorded = sendEvidence && isSent(sendEvidence) && sendEvidence.kind !== 'provider_accepted'
    ? sendEvidence : null;
  const labelAsKeystone = !!sendEvidence;

  if (sends.length === 0 && recorded) {
    return (
      <div className="space-y-2 py-2" data-testid="send-history-list">
        <RecordedSendItem evidence={recorded} />
      </div>
    );
  }

  if (sends.length === 0) {
    return (
      <div className="text-center py-8" data-testid="send-history-empty">
        <Mail className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">No emails sent yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 py-2" data-testid="send-history-list">
      <div className="text-xs text-muted-foreground mb-2">
        {data?.total} email{data?.total === 1 ? '' : 's'} sent
      </div>
      {sends.map((send) => (
        <SendHistoryItem key={send.id} send={send} labelAsKeystone={labelAsKeystone} />
      ))}
      {recorded && <RecordedSendItem evidence={recorded} />}
    </div>
  );
}
